/**
 * JSON-LD Export Utility
 *
 * Converts an ontology graph (nodes + edges) into a JSON-LD document
 * following the SRO (Structured Representation Ontology) format.
 *
 * This utility is shared between frontend (for in-browser export) and
 * backend (for API-based export of saved ontologies).
 */

/**
 * Input node representation (compatible with both frontend ConceptNodeData and backend DB rows)
 */
export interface ExportNode {
  id: string;
  label: string;
  definition?: string;
  properties?: (string | { name: string; type?: string })[];
}

/**
 * Input edge representation
 */
export interface ExportEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
}

/**
 * Options for JSON-LD export
 */
export interface JsonLdExportOptions {
  /** The ontology namespace URI (default: http://example.org/ontology#) */
  namespace?: string;
  /** The ontology namespace prefix (default: onto) */
  prefix?: string;
  /** The ontology name/title */
  name?: string;
  /** The ontology description */
  description?: string;
}

/**
 * JSON-LD context type definition
 */
interface JsonLdContext {
  [key: string]: string | { '@id': string; '@type'?: string };
}

/**
 * JSON-LD graph entity
 */
interface JsonLdEntity {
  id: string;
  type: string;
  name: string;
  description?: string;
  attributes?: Record<string, string>;
  relatesTo?: string[];
  [key: string]: unknown;
}

/**
 * Complete JSON-LD document
 */
export interface JsonLdDocument {
  '@context': JsonLdContext;
  '@graph': JsonLdEntity[];
}

/**
 * Sanitize a string for use as an IRI local name.
 * Converts to PascalCase and removes non-alphanumeric characters.
 */
function toIriLocalName(label: string): string {
  return label
    .replace(/[^a-zA-Z0-9\s_-]/g, '')
    .split(/[\s_-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
}

/**
 * Convert ontology graph data to a JSON-LD document.
 *
 * Maps:
 * - Nodes → Entity entries in @graph with attributes from properties
 * - Edges → Operation entries in @graph + relatesTo arrays on source entities
 *
 * @param nodes - Array of ontology nodes
 * @param edges - Array of ontology edges
 * @param options - Export configuration options
 * @returns A JSON-LD document object
 */
export function convertToJsonLd(
  nodes: ExportNode[],
  edges: ExportEdge[],
  options: JsonLdExportOptions = {}
): JsonLdDocument {
  const {
    namespace = 'http://example.org/ontology#',
    prefix = 'onto',
    name: _ontologyName,
    description: _ontologyDescription,
  } = options;

  // Build the @context
  const context: JsonLdContext = {
    id: '@id',
    type: '@type',
    name: 'schema:name',
    description: 'schema:description',
    schema: 'http://schema.org/',
    [prefix]: namespace,
    Entity: `${prefix}:Entity`,
    Operation: `${prefix}:Operation`,
    attributes: `${prefix}:attributes`,
    relatesTo: {
      '@id': `${prefix}:relatesTo`,
      '@type': '@id',
    },
    properties: `${prefix}:properties`,
  };

  // Build a lookup from node ID to IRI local name
  const nodeIriMap = new Map<string, string>();
  nodes.forEach((node) => {
    const localName = toIriLocalName(node.label);
    nodeIriMap.set(node.id, `${prefix}:${localName}`);
  });

  // Build edge lookup: source → list of target IRIs + labels
  const outgoingEdges = new Map<string, { targetIri: string; label: string }[]>();
  edges.forEach((edge) => {
    const targetIri = nodeIriMap.get(edge.target);
    if (!targetIri) return;

    const existing = outgoingEdges.get(edge.source) || [];
    existing.push({
      targetIri,
      label: edge.label || 'relates_to',
    });
    outgoingEdges.set(edge.source, existing);
  });

  // Build @graph entries for nodes (Entities)
  const graphEntities: JsonLdEntity[] = nodes.map((node) => {
    const iri = nodeIriMap.get(node.id)!;
    const entity: JsonLdEntity = {
      id: iri,
      type: 'Entity',
      name: node.label,
    };

    if (node.definition) {
      entity.description = node.definition;
    }

    // Convert properties to attributes map
    if (node.properties && node.properties.length > 0) {
      const attributes: Record<string, string> = {};
      node.properties.forEach((prop) => {
        if (typeof prop === 'string') {
          attributes[prop] = 'string';
        } else {
          attributes[prop.name] = prop.type || 'string';
        }
      });
      entity.attributes = attributes;
    }

    // Add relatesTo from edges
    const relations = outgoingEdges.get(node.id);
    if (relations && relations.length > 0) {
      entity.relatesTo = [...new Set(relations.map((r) => r.targetIri))];
    }

    return entity;
  });

  // Build @graph entries for edges (Operations) — deduplicated by label
  const seenOperationLabels = new Set<string>();
  const graphOperations: JsonLdEntity[] = [];

  edges.forEach((edge) => {
    const operationLabel = edge.label || 'relates_to';
    const operationName = toIriLocalName(operationLabel);

    // Deduplicate operations with the same label
    if (seenOperationLabels.has(operationName)) return;
    seenOperationLabels.add(operationName);

    const sourceNode = nodes.find((n) => n.id === edge.source);
    const targetNode = nodes.find((n) => n.id === edge.target);

    const operation: JsonLdEntity = {
      id: `${prefix}:${operationName}`,
      type: 'Operation',
      name: operationLabel,
      description: `Relates ${sourceNode?.label || 'source'} to ${targetNode?.label || 'target'}`,
    };

    graphOperations.push(operation);
  });

  // Combine all graph entries
  const graph = [...graphEntities, ...graphOperations];

  return {
    '@context': context,
    '@graph': graph,
  };
}

/**
 * Serialize a JSON-LD document to a formatted JSON string.
 */
export function serializeJsonLd(document: JsonLdDocument): string {
  return JSON.stringify(document, null, 2);
}

/**
 * Parse a JSON-LD document (as produced by `convertToJsonLd`) back into ontology
 * nodes + edges — the inverse of `convertToJsonLd`, used to IMPORT an ontology.
 *
 * Mapping:
 * - `@graph` entries of `type: 'Entity'` → nodes (id = IRI, label = name,
 *   definition = description, properties from the `attributes` map).
 * - each entity's `relatesTo` array → edges (source entity IRI → target IRI).
 *
 * Note (round-trip limitation): the export dedupes edge labels into `Operation`
 * entries and stores only unlabeled `relatesTo` targets, so per-edge relationship
 * labels are not preserved. If the document has exactly one `Operation` we use
 * its name for all edges; otherwise edges default to `relates_to`. Node ids are
 * the IRIs and are only used to wire edges — the importer assigns fresh node_ids.
 */
export function parseJsonLd(document: JsonLdDocument): {
  nodes: ExportNode[];
  edges: ExportEdge[];
} {
  const graph = Array.isArray(document?.['@graph']) ? document['@graph'] : [];
  const isType = (e: JsonLdEntity, t: string) =>
    typeof e.type === 'string' && (e.type === t || e.type.endsWith(`:${t}`));

  const entities = graph.filter((e) => isType(e, 'Entity'));
  const operations = graph.filter((e) => isType(e, 'Operation'));
  const singleOpLabel =
    operations.length === 1 && operations[0]?.name ? String(operations[0].name) : null;

  const nodes: ExportNode[] = entities.map((e) => {
    const attrs =
      e.attributes && typeof e.attributes === 'object'
        ? (e.attributes as Record<string, unknown>)
        : {};
    return {
      id: String(e.id),
      label: String(e.name ?? e.id),
      definition: e.description ? String(e.description) : undefined,
      properties: Object.entries(attrs).map(([name, type]) => ({
        name,
        type: type ? String(type) : 'string',
      })),
    };
  });

  const edges: ExportEdge[] = [];
  let ei = 0;
  for (const e of entities) {
    const rel = e.relatesTo;
    const targets = Array.isArray(rel) ? rel : rel ? [rel] : [];
    for (const target of targets) {
      edges.push({
        id: `edge-${ei++}`,
        source: String(e.id),
        target: String(target),
        label: singleOpLabel ?? 'relates_to',
      });
    }
  }

  return { nodes, edges };
}
